import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { gql, useMutation } from '@apollo/client';
import { X } from 'lucide-react';

/* ─── GraphQL ──────────────────────────────────────────────────────── */

// Re-uses the same idempotent mutation that powers initial sharing — passing
// the existing vault_document_id + deal_team_id with a new shared_with_roles
// array overwrites the previous value. See
// app/graphql/mutations/deal_team/share_vault_document.rb.
const SHARE_VAULT_DOCUMENT = gql`
  mutation ShareVaultDocumentWithDealTeam(
    $vault_document_id: ID!
    $deal_team_id: ID!
    $shared_with_roles: [String!]
  ) {
    share_vault_document_with_deal_team(
      vault_document_id: $vault_document_id
      deal_team_id: $deal_team_id
      shared_with_roles: $shared_with_roles
    ) {
      vault_document {
        id
        shared_with_roles
      }
      errors
    }
  }
`;

/* ─── Constants ────────────────────────────────────────────────────── */

// Identical to ShareSnapshotModal/DealTeamUploadModal — keep these three
// in sync if/when roles change.
const ROLE_OPTIONS = [
  { value: 'cbv', label: 'Valuation Specialist (CBV)' },
  { value: 'business_broker', label: 'Business Broker' },
  { value: 'lawyer', label: 'Lawyer' },
  { value: 'accountant', label: 'Accountant' },
  { value: 'lender', label: 'Lender' },
  { value: 'cepa_coach', label: 'CEPA Coach' },
];

/* ─── ManageAccessModal ────────────────────────────────────────────── */

const ManageAccessModal = ({
  open,
  onClose,
  onSuccess,
  dealTeamId,
  vaultDocumentId,
  documentTitle,
  currentSharedWithRoles,
}) => {
  // Restrict-by-role toggle reflects current state on open: ON if the doc
  // already has any role restrictions, OFF if it's open to the whole team.
  const [restrictByRole, setRestrictByRole] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [shareVaultDocument] = useMutation(SHARE_VAULT_DOCUMENT);

  // Hydrate from `currentSharedWithRoles` whenever the modal opens. Reset
  // back to neutral on close so the next open is clean (mirrors
  // ShareSnapshotModal / DealTeamUploadModal).
  useEffect(() => {
    if (open) {
      const roles = currentSharedWithRoles || [];
      setRestrictByRole(roles.length > 0);
      setSelectedRoles([...roles]);
      setError(null);
      setSubmitting(false);
    } else {
      setRestrictByRole(false);
      setSelectedRoles([]);
      setError(null);
      setSubmitting(false);
    }
  }, [open, currentSharedWithRoles]);

  if (!open) return null;

  const toggleRole = (role) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const canSubmit =
    !submitting && !(restrictByRole && selectedRoles.length === 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);

    try {
      // Empty array = open to all team members. Backend enum-validates roles.
      const rolesToShare = restrictByRole ? selectedRoles : [];

      const result = await shareVaultDocument({
        variables: {
          vault_document_id: vaultDocumentId,
          deal_team_id: dealTeamId,
          shared_with_roles: rolesToShare,
        },
      });

      const payload = result.data?.share_vault_document_with_deal_team;
      const errs = payload?.errors || [];
      if (errs.length > 0) throw new Error(errs.join(', '));
      if (!payload?.vault_document?.id) {
        throw new Error('Update did not return a document id.');
      }

      if (window.ahoy) {
        window.ahoy.track('succeedo_deal_team_document_access_changed', {
          deal_team_id: dealTeamId,
          vault_document_id: vaultDocumentId,
          shared_with_roles: rolesToShare,
        });
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Update failed. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="deal-team-manage-access-title"
    >
      <div className="w-full max-w-md bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-[hsl(var(--border))]">
          <div className="min-w-0">
            <h2
              id="deal-team-manage-access-title"
              className="font-serif text-[20px] font-light text-[hsl(var(--foreground))]"
            >
              Manage access
            </h2>
            {documentTitle && (
              <p className="text-[12px] text-[hsl(var(--muted-foreground))] mt-0.5 truncate">
                {documentTitle}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Restrict by role (mirrors ShareSnapshotModal / DealTeamUploadModal) */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={restrictByRole}
                onChange={(e) => setRestrictByRole(e.target.checked)}
                disabled={submitting}
                className="w-4 h-4 rounded border-[hsl(var(--border))] accent-[hsl(var(--succeedo-orange))]"
              />
              <span className="text-[13px] text-[hsl(var(--foreground))]">
                Restrict access by role
              </span>
            </label>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1 ml-6">
              {restrictByRole
                ? 'Only checked roles will see this document.'
                : 'All team members will be able to view this document.'}
            </p>

            {restrictByRole && (
              <div className="mt-3 ml-6 grid grid-cols-1 gap-1.5">
                {ROLE_OPTIONS.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedRoles.includes(opt.value)}
                      onChange={() => toggleRole(opt.value)}
                      disabled={submitting}
                      className="w-4 h-4 rounded border-[hsl(var(--border))] accent-[hsl(var(--succeedo-orange))]"
                    />
                    <span className="text-[13px] text-[hsl(var(--foreground))]">
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="text-[12px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[hsl(var(--border))]">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-[13px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-4 py-2 rounded-full text-[13px] font-medium bg-[hsl(var(--succeedo-orange))] text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {submitting ? 'Updating…' : 'Update access'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

ManageAccessModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  dealTeamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  vaultDocumentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  documentTitle: PropTypes.string.isRequired,
  currentSharedWithRoles: PropTypes.arrayOf(PropTypes.string),
};

ManageAccessModal.defaultProps = {
  onSuccess: undefined,
  currentSharedWithRoles: [],
};

export default ManageAccessModal;
